import type { JSX } from 'react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { DrawType } from '@apply/constants';
import { BrokerClientApi, DrawApi } from '@apply/api';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import {
    Button,
    ButtonTheme,
    Panel,
    PanelBorderRadius,
    PanelTheme,
    ToggleButton,
    ToggleButtonItem,
    BankAccountBox,
} from '@apply/components';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import { GenericPopup } from '@apply/components';

export interface IRequestDrawModalV2Props {
    drawBudgetId: number | undefined;
    isOutOfBalance: boolean;
    isOutOfBalanceWaived: boolean;
    currentAccountNumber: string | undefined;
    percentFunded: number | undefined;
    open: boolean;
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    submitDrawRequest: ({
        drawBudgetId,
        drawType,
        fontId,
        ipAddress,
    }: {
        drawBudgetId: number;
        drawType: DrawType;
        fontId: number | undefined;
        ipAddress: string | undefined;
    }) => void;
    handleCloseEvent: () => void;
    isAdmin?: boolean;
}

export const RequestDrawModalV2 = ({
    drawBudgetId,
    isOutOfBalance,
    isOutOfBalanceWaived,
    open,
    applicationInsights,
    percentFunded,
    submitDrawRequest,
    handleCloseEvent,
    currentAccountNumber,
    isAdmin = false,
}: IRequestDrawModalV2Props): JSX.Element => {
    const navigate = useNavigate();

    const manualNotAvailableText = '* Manual not available. Prior draw was over funded.';

    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const [isAutomatic, set_isAutomatic] = useState<boolean>(true);
    const [enteredAccountNumber, set_enteredAccountNumber] = useState<string>();
    const [isAccountNumberValid, set_isAccountNumberValid] = useState<boolean>(false);

    const [isManual, set_isManual] = useState<boolean>(false);
    const [disableSubmit, set_disableSubmit] = useState<boolean>(false);

    const [selectedDrawType, set_selectedDrawType] = useState('');

    const onAutomaticButtonClick = () => {
        set_isAutomatic(true);
        set_isManual(false);
    };

    const onManualButtonClick = () => {
        set_isAutomatic(false);
        set_isManual(true);
    };

    const handleSubmit = async () => {
        set_disableSubmit(true);

        try {
            if (isAutomatic && !isManual) {
                if (isAdmin) {
                    if (!drawBudgetId) return;

                    submitDrawRequest({
                        drawBudgetId,
                        drawType: DrawType.Automatic,
                        fontId: undefined,
                        ipAddress: undefined,
                    });
                } else {
                    brokerClientApi.getIpAddress().then((response) => {
                        if (response.statusCode !== 200) return;

                        onConfirmAutomaticAgreement();
                    });
                }
            }

            if (!isAutomatic && isManual) {
                if (!drawBudgetId) return;
                if (!enteredAccountNumber) return;

                if (currentAccountNumber?.toString() !== enteredAccountNumber) {
                    drawApi.drawBudgetBankAccountLast4DigitsOn(drawBudgetId, enteredAccountNumber);
                }

                // Wait for draw request before redirecting
                await submitDrawRequest({
                    drawBudgetId,
                    drawType: DrawType.Manual,
                    fontId: undefined,
                    ipAddress: undefined,
                });
                navigate(`/draw/${drawBudgetId}`);
            }

            if ((isAutomatic && isManual) || (!isAutomatic && !isManual)) {
                throw new Error('Invalid state for request draw modal.');
            }
        } finally {
            set_disableSubmit(false);
        }
    };

    const handleClose = () => {
        set_selectedDrawType('');
        set_enteredAccountNumber(undefined);
        handleCloseEvent();
    };

    const onConfirmAutomaticAgreement = () => {
        if (!drawBudgetId) return;
        if (!enteredAccountNumber) return;

        if (currentAccountNumber?.toString() !== enteredAccountNumber) {
            drawApi.drawBudgetBankAccountLast4DigitsOn(drawBudgetId, enteredAccountNumber);
        }

        navigate(`/mobiledrawsign/${drawBudgetId}/`, { state: { automatic: true } });
    };

    const canSubmit = () => {
        if (selectedDrawType == '' || selectedDrawType == undefined || selectedDrawType == null) return false;
        if (isAdmin) return true;

        return isAccountNumberValid;
    };

    const bankAccountChanged = (enteredAccount: string) => {
        const cleanedAccount = ('' + enteredAccount).replace(/\D/g, '');
        set_enteredAccountNumber(cleanedAccount);
    };

    const lateStageDrawThreshold = () => {
        if ((percentFunded ?? 0) > 80) {
            return true;
        }
        return false;
    };

    const toggleButtonDrawTypeItems: ToggleButtonItem[] = [
        {
            id: 'Automatic',
            text: 'Automatic',
        },
        {
            id: 'Manual',
            text: 'Manual',
            disabled: isOutOfBalance && !isOutOfBalanceWaived,
            hint: isOutOfBalance && !isOutOfBalanceWaived ? manualNotAvailableText : '',
        },
    ];

    const toggleButtonDrawTypeClick = (e: ButtonGroupTypes.ItemClickEvent) => {
        switch (e.itemData?.id) {
            case 'Automatic':
                onAutomaticButtonClick();
                set_selectedDrawType(e.itemData.id);
                return;
            case 'Manual':
                onManualButtonClick();
                set_selectedDrawType(e.itemData.id);
                return;
            default:
                return;
        }
    };

    const actionButtons = [
        {
            id: 'submitDrawRequestButton',
            theme: ButtonTheme.Primary,
            children: 'Submit Request',
            width: '70%',
            disabled: !canSubmit() || disableSubmit,
            handleClick: handleSubmit,
        },
    ];

    const drawSelectorDialog = (
        <>
            <div className='drawWrapper'>
                <div className='drawTypeLabel'>Select Draw Type: *</div>
                <div className='drawTypeSelector'>
                    <ToggleButton
                        id='toggleButtonDrawTypeSelection'
                        items={toggleButtonDrawTypeItems}
                        selectedItems={[selectedDrawType]}
                        selectionMode='single'
                        stylingMode='outlined'
                        onItemClick={toggleButtonDrawTypeClick}
                        width='100%'
                        height='40px'
                    />
                </div>
                {
                    // Out of Balance alert
                    isOutOfBalance && !isOutOfBalanceWaived && (
                        <Panel
                            theme={PanelTheme.Red}
                            borderRadius={PanelBorderRadius.Medium}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <b>Manual Not Available</b>
                                <br />
                                Prior draw was over funded.
                            </div>
                        </Panel>
                    )
                }
                {
                    // Late stage draw alert
                    lateStageDrawThreshold() && (
                        <Panel
                            theme={PanelTheme.Red}
                            borderRadius={PanelBorderRadius.Medium}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <b>Automatic Recommended</b>
                                <br />
                                Inspection required for final draw.
                            </div>
                        </Panel>
                    )
                }
                {!isAdmin && (
                    <>
                        <div className='drawInputRow drawFlexRow'>
                            <BankAccountBox
                                value={enteredAccountNumber ? enteredAccountNumber : ''}
                                label='Enter last 4 of bank account:'
                                onChange={bankAccountChanged}
                                onValidationEvent={(isValid) => set_isAccountNumberValid(isValid)}
                                width='100%'
                                maxLength={4}
                                required={true}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );

    return (
        <>
            <GenericPopup
                id='drawRequestPopup'
                isOpen={open}
                actionButtons={actionButtons}
                handleClose={handleClose}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title='Draw Request'
                width='600px'
                maxWidth='90%'
                maxHeight='80%'
                buttonAlignment='center'
                wrapTitle={true}
            >
                {drawSelectorDialog}
            </GenericPopup>
        </>
    );
};
